import FormCompanyImage from 'components/form/FormCompanyImage';
import FormSelect from 'components/form/FormSelect';
import FormText from 'components/form/FormText';
import FormWysiwygEditor from 'components/form/FormWysiwygEditor';
import IndicatorScreen from 'components/indicator/IndicatorScreen';

import { useAppState } from 'hooks/useAppState';
import { useState, type JSX } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAtom } from 'jotai';
import { AcctState } from 'stores/atoms/acctState';
import type { DiagnosisEditFormStateType } from 'types/page/diagnosisEditFormStateType';
import type { FormEditFormStateType } from 'types/page/formEditFormStateType';
import type { QuizEditFormStateType } from 'types/page/quizEditFormStateType';
import type { VoiceChatEditFormStateType } from 'types/page/voiceChatEditFormStateType';
import WsModalBenchmarkContactListId from 'pages/ws/components/contentEditBase/WsModalBenchmarkContactListId';
import { formatRateToPercent, formatTimeToDate, getDivSelectOptions } from 'utils/app';

/**
 * コンテンツ作成編集 基本情報設定コンポーネント
 */
export default function WsContentEditBase(props: {
  readOnly: boolean;
  contentCategory: { label: string; value: number };
  onCreate: () => void;
  processing: boolean;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */
  const [acctState] = useAtom(AcctState);
  const { isAiTextCountNoLimit, getActivePlan } = useAppState();

  const [modalBenchmarkOpenState, setModalBenchmarkOpenState] = useState(false);

  const formContext = useFormContext();
  let typedFormContext;
  if (props.contentCategory.label === 'クイズ') {
    typedFormContext = formContext as unknown as ReturnType<
      typeof useFormContext<QuizEditFormStateType>
    >;
  } else if (props.contentCategory.label === '診断') {
    typedFormContext = formContext as unknown as ReturnType<
      typeof useFormContext<DiagnosisEditFormStateType>
    >;
  } else if (props.contentCategory.label === 'フォーム') {
    typedFormContext = formContext as unknown as ReturnType<
      typeof useFormContext<FormEditFormStateType>
    >;
  } else if (props.contentCategory.label === 'ボイスチャット') {
    typedFormContext = formContext as unknown as ReturnType<
      typeof useFormContext<VoiceChatEditFormStateType>
    >;
  } else {
    throw new Error('無効なコンテンツカテゴリ');
  }
  const {
    control,
    register,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = typedFormContext;

  const activePlan = getActivePlan();

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const descriptionPaceHolder =
    {
      クイズ: 'クイズをしたくなる説明文を入れよう',
      診断: '対象ユーザー、診断の目的、結果で得られる情報について具体的に書いてください。',
      フォーム: '対象ユーザー、フォームの目的、結果で得られる情報について具体的に書いてください。',
      ボイスチャット:
        '対象ユーザー、ボイスチャットの目的、結果で得られる情報について具体的に書いてください。',
    }[props.contentCategory.label] || '';
  const aiButtonLabel =
    {
      クイズ: 'AIによるクイズ生成のデモ',
      診断: 'AIによる診断型コンテンツ生成のデモ',
      フォーム: 'AIによるフォーム型コンテンツ生成のデモ',
      ボイスチャット: 'AIによるボイスチャット型コンテンツ生成のデモ',
    }[props.contentCategory.label] || '';

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */

  /**
   * URLを別ウィンドウで開きます。
   */
  const onOpenUrl = (url: string) => {
    if (!url) {
      return;
    }
    window.open(url, '_blank');
  };

  /**
   * URLを別ウィンドウで開きます。
   */
  const onEditBenchmarkContactListId = () => {
    setModalBenchmarkOpenState(true);
  };

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * AIテキスト生成数の上限に達しているかどうかを判定します。
   */
  const _isAiTextCountLimit = (): boolean => {
    return acctState.company.aiTextUsageCount >= (activePlan?.aiTextCountLimit ?? 0);
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {/* ローディング */}
      <IndicatorScreen visible={props.processing} />

      <section className="section-form-default">
        <div className="container">
          <div className="box-common shadow-none">
            <h3 className="title-section title-md">基本設定</h3>

            <div className="form-default-container">
              <div className="row row-0">
                <div className="col-md col-header">
                  <div className="card form-header">
                    <h4 className="title">表紙の画像</h4>
                    <FormCompanyImage control={control} name="imageKey" readOnly={props.readOnly} />
                  </div>
                </div>
                <div className="col-md col-form">
                  <div className="form-group">
                    <FormText
                      subject={`${props.contentCategory.label}のタイトル`}
                      name="title"
                      placeholder="タイトルを入れよう"
                      register={register}
                      errors={errors}
                      required={true}
                      maxLength={100}
                      readOnly={props.readOnly}
                    />
                  </div>
                  <div className="form-group">
                    <input
                      id="titleHiddenFlag"
                      name="titleHiddenFlag"
                      type="checkbox"
                      className="form-check-input" // Bootstrapのチェックボックス用クラス
                      {...register('titleHiddenFlag')}
                    />
                    <label htmlFor="titleHiddenFlag" className="form-label ms-2">
                      {`トップの${props.contentCategory.label}タイトルを表示しない`}
                    </label>
                  </div>
                  <div className="form-group">
                    <FormWysiwygEditor
                      control={control}
                      subject="説明文"
                      name="description"
                      placeholder={descriptionPaceHolder}
                      toolbar={['highlight']}
                      errors={errors}
                      readOnly={props.readOnly}
                    />
                    <p className="text-gray" style={{ fontSize: '14px' }}>
                      ※ &lt; &gt; で囲んだテキストはmeta descriptionから削除されます。
                    </p>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{aiButtonLabel}</label>
                    <div className="row align-items-center">
                      <div className="col-md ">
                        <div className="form-label d-flex justify-content-between">
                          <span>テキスト生成数</span>
                          <span>
                            {acctState.company.aiTextUsageCount} /{' '}
                            {isAiTextCountNoLimit()
                              ? '無制限'
                              : (activePlan?.aiTextCountLimit ?? 0)}
                          </span>
                        </div>
                        <div
                          className="progress"
                          role="progressbar"
                          aria-valuenow={30}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        >
                          <div
                            className="progress-bar"
                            style={{
                              width: `${
                                isAiTextCountNoLimit()
                                  ? 0
                                  : formatRateToPercent(
                                      acctState.company.aiTextUsageCount,
                                      activePlan?.aiTextCountLimit ?? 0
                                    )
                              }%`,
                            }}
                          ></div>
                        </div>
                        <div className="form-label">
                          <span>
                            ※{formatTimeToDate(acctState.company.usageCountClearAt, 'yyyy/MM/dd')}
                            にリセットされます
                          </span>
                        </div>
                      </div>
                      <div className="col-md col-btn flex-grow-0">
                        <div
                          className={`btn-container mt-md-0 ${
                            _isAiTextCountLimit() ? '' : 'max-width-130'
                          }`}
                        >
                          <button
                            onClick={props.onCreate}
                            className="btn btn-common ai-generate w-100"
                            disabled={props.readOnly || _isAiTextCountLimit()}
                          >
                            {_isAiTextCountLimit() ? '上限に達しました' : 'AIで生成する'}
                          </button>
                        </div>
                        {_isAiTextCountLimit() && (
                          <a
                            className="detail hover d-flex align-items-center justify-content-center mt-2"
                            target="_blank"
                            href="https://docs.google.com/forms/d/e/1FAIpQLSfDjNaMmUwd6_eSvS4rtrrYjTbJgYibVDKiIbazj5wHclEdHQ/viewform?usp=sf_link"
                          >
                            <img
                              src="/img/icon/exclamation.svg"
                              width={18}
                              className="me-1"
                              style={{ verticalAlign: 'text-top' }}
                            />
                            プランアップデート
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="privacy-policy" className="form-label">
                      プライバシーポリシーのURL
                    </label>
                    <div className="row align-items-center">
                      <div className="col-md">
                        <FormText
                          name="privacyPolicyUrl"
                          placeholder="http://example.com/plivacypolicy"
                          register={register}
                          errors={errors}
                          readOnly={true}
                        />
                      </div>
                      <div className="col-md col-btn flex-grow-0">
                        <div className="btn-container max-width-130 mt-md-0">
                          <button
                            onClick={() => onOpenUrl(getValues().privacyPolicyUrl)}
                            disabled={!getValues().privacyPolicyUrl}
                            className="btn btn-common moderate-blue w-100"
                          >
                            URLの確認
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="terms" className="form-label">
                      利用規約のURL
                    </label>
                    <div className="row align-items-center">
                      <div className="col-md">
                        <FormText
                          name="termsUrl"
                          placeholder="http://example.com/terms"
                          register={register}
                          errors={errors}
                          readOnly={true}
                        />
                      </div>
                      <div className="col-md col-btn flex-grow-0">
                        <div className="btn-container max-width-130 mt-md-0">
                          <button
                            onClick={() => onOpenUrl(getValues().termsUrl)}
                            disabled={!getValues().termsUrl}
                            className="btn btn-common moderate-blue w-100"
                          >
                            URLの確認
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <FormText
                      subject="コピーライト"
                      name="copyright"
                      placeholder="（C）BranQ 2023"
                      register={register}
                      errors={errors}
                      readOnly={true}
                    />
                  </div>
                  {/* LINE連携: 実DBに line_* カラムが無いボイスチャットでは非表示（form/クイズ/診断のみ表示） */}
                  {props.contentCategory.label !== 'ボイスチャット' && (
                    <>
                      <div className="form-group">
                        <FormSelect
                          subject="LINE連携"
                          name="lineFlag"
                          register={register}
                          errors={errors}
                          options={getDivSelectOptions('lineFlag')}
                          disabled={props.readOnly}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="privacy-policy" className="form-label">
                          LINE友だち追加URL
                        </label>
                        <div className="row align-items-center">
                          <div className="col-md">
                            <FormText
                              name="lineUrl"
                              placeholder="https://lin.ee/xxxxxxx"
                              register={register}
                              errors={errors}
                              readOnly={props.readOnly}
                            />
                          </div>
                          <div className="col-md col-btn flex-grow-0">
                            <div className="btn-container max-width-130 mt-md-0">
                              <button
                                onClick={() => onOpenUrl(getValues().lineUrl)}
                                disabled={!watch().lineUrl}
                                className="btn btn-common moderate-blue w-100"
                              >
                                URLの確認
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  <div className="form-group">
                    <label htmlFor="privacy-policy" className="form-label">
                      Benchmark コンタクトリストID
                    </label>
                    <div className="row align-items-center">
                      <div className="col-md">
                        <FormText
                          name="benchmarkContactListId"
                          register={register}
                          errors={errors}
                          readOnly={true}
                        />
                      </div>
                      <div className="col-md col-btn flex-grow-0">
                        <div className="btn-container max-width-130 mt-md-0">
                          <button
                            onClick={onEditBenchmarkContactListId}
                            disabled={props.readOnly}
                            className="btn btn-common moderate-blue w-100"
                          >
                            編集
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <FormText
                      subject={`GTM（Google Tag Manager）ID`}
                      name="gtmId"
                      placeholder="GTM-XXXXXXXX"
                      register={register}
                      errors={errors}
                      maxLength={100}
                      readOnly={props.readOnly}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ベンチマークコンタクトリストID編集モーダル */}
      <WsModalBenchmarkContactListId
        setOpen={setModalBenchmarkOpenState}
        open={modalBenchmarkOpenState}
        apiKey={acctState.company.apiKeyBenchmark}
        contactListId={getValues().benchmarkContactListId}
        onSubmit={attr => {
          setValue('benchmarkContactListId', attr.contactListId);
        }}
      />
    </>
  );
}
